import {
  CellClassParams,
  ProcessCellForExportParams,
} from "@ag-grid-community/core";
import { AgGridReact, AgGridReactProps } from "@ag-grid-community/react";
import {
  Box,
  forwardRef,
  SystemStyleObject,
  useMergeRefs,
} from "@chakra-ui/react";
import { FunctionComponent, useCallback, useEffect, useMemo } from "react";
import { EmptyStateProps } from "shared/components/empty/EmptyState";
import { useGetMaybernColumns } from "shared/components/table/maybern/MaybernTableColumnTypes";
import { MaybernTableDataTypeDefinitions } from "shared/components/table/maybern/MaybernTableDataTypeDefinitions";
import { getMaybernTableDefaultColDef } from "shared/components/table/maybern/MaybernTableDefaultColDef";
import {
  MaybernTableContextWithErrors,
  MaybernTableGetErrorForCellFn,
} from "shared/components/table/maybern/MaybernTableErrors";
import { MaybernTableExcelStyle } from "shared/components/table/maybern/MaybernTableExcelStyle";
import {
  LoadingOverlayComponent,
  NoRowsOverlayComponent,
  OverlayProps,
} from "shared/components/table/maybern/MaybernTableOverlay";
import { useMaybernTableUIContext } from "shared/components/table/maybern/MaybernTableUIContext";
import { tryGetAgGridApi } from "shared/components/table/maybern/MaybernTableUtils";
import { useToast } from "shared/components/toast/useToast";
import { useMergedProps } from "shared/utils/ReactUtils";

export type MaybernTableContext<TData> = MaybernTableContextWithErrors<TData>;

export interface MaybernTableProps<TData> extends AgGridReactProps<TData> {
  getErrorForCell?: MaybernTableGetErrorForCellFn<TData>;
  sx?: SystemStyleObject;
  emptyStateProps?: EmptyStateProps;
  cellClassRules?: Record<string, (params: CellClassParams) => boolean>;
}

// This type is needed so the generic type args are not lost when using forwardRef
export type MaybernTable = <TData>(
  props: MaybernTableProps<TData> & {
    ref?: React.Ref<AgGridReact<TData>>;
  },
) => React.ReactElement | null;

// eslint-disable-next-line no-redeclare,react/display-name
export const MaybernTable = forwardRef(
  (
    {
      getErrorForCell,
      getRowId,
      sx,
      emptyStateProps,
      cellClassRules,
      ...props
    }: MaybernTableProps<unknown>,
    fwRef,
  ) => {
    const { agGridRef, setIsReady } = useMaybernTableUIContext();
    const mergedRef = useMergeRefs(fwRef, agGridRef);

    const agGrid = agGridRef.current;

    const onGridReady = useCallback(() => {
      setIsReady(true);
    }, [setIsReady]);

    const context = useMemo<MaybernTableContext<unknown>>(
      () => ({
        getErrorForCell,
      }),
      [getErrorForCell],
    );

    const overlayProps: OverlayProps = {
      emptyStateProps,
    };
    const { showToast } = useToast();

    const defaultColDef = useMemo(
      () => ({
        ...getMaybernTableDefaultColDef(cellClassRules ?? {}),
      }),
      [cellClassRules],
    );

    const mergedProps = useMergedProps<AgGridReactProps>(
      {
        columnTypes: useGetMaybernColumns(showToast),
        context,
        dataTypeDefinitions: MaybernTableDataTypeDefinitions,
        defaultColDef,
        excelStyles: MaybernTableExcelStyle,
        loadingOverlayComponent: LoadingOverlayComponent,
        loadingOverlayComponentParams: overlayProps,
        noRowsOverlayComponent: NoRowsOverlayComponent,
        noRowsOverlayComponentParams: overlayProps,
        onGridReady,
        reactiveCustomComponents: true,
        getRowId: getRowId ?? undefined,
        groupHeaderHeight: 32,
        suppressContextMenu: true,
        stopEditingWhenCellsLoseFocus: true,
        processCellFromClipboard: useCallback(
          (params: ProcessCellForExportParams) => {
            if (params.value === "") return null;
            return params.parseValue(params.value);
          },
          [],
        ),
      },
      props,
    );

    // Refresh the grid when the context changes.
    // This does not happen automatically, see sample code at:
    // https://www.ag-grid.com/javascript-data-grid/context/
    useEffect(() => {
      void context;
      const api = tryGetAgGridApi(agGrid);
      if (api) {
        api.refreshHeader();
        api.redrawRows();
      }
    }, [agGrid, context]);

    return (
      <Box className="ag-theme-maybern" sx={sx}>
        <AgGridReact ref={mergedRef} {...mergedProps} />
      </Box>
    );
  },
) as MaybernTable;

(MaybernTable as FunctionComponent).displayName = "MaybernTable";
